import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { ComplianceStore } from './ComplianceStore'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { Checkbox } from 'antd'
import { ComplianceInfo } from './ComplianceInfo'
import { ComplianceMonthSelector } from './ComplianceMonthSelector'
import { Select } from 'antd-v4'
import { BaseComplianceModels } from '@getgreenline/compliance'
import { CompaniesModels } from '@getgreenline/companies'
import { ComplianceQuarterSelector } from './ComplianceQuarterSelector'
import { monthType } from '../../../../components/Input/MonthSelector'
import { FormError } from '../../../../components/FormError'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface ILocationAndMonthSelectorProps {
  complianceStore: ComplianceStore
}

interface State {
  errorMessage: string
}

@inject('currentCompanyStore')
@observer
export class LocationAndMonthSelector extends React.Component<
  InjectedProps & ILocationAndMonthSelectorProps,
  State
> {
  constructor(props: InjectedProps & ILocationAndMonthSelectorProps) {
    super(props)

    this.state = {
      errorMessage: '',
    }
  }

  componentDidMount = async () => {
    // if there is only 1 location, auto-enable it
    const { currentCompanyStore, complianceStore } = this.props

    if (!currentCompanyStore) {
      return
    }

    if (currentCompanyStore.locations && currentCompanyStore.locations.length === 1) {
      const defaultLocationId = currentCompanyStore.locations[0].id
      if (!complianceStore.locationIds.includes(defaultLocationId)) {
        complianceStore.toggleLocationId(currentCompanyStore.locations[0].id)
      }
    }

    await currentCompanyStore.getCanUseNewMBComplianceReport()
  }

  onClickNext = () => {
    const { month, year, locationIds, fromCurrentStepToNextStep } = this.props.complianceStore

    if (!month || !year || locationIds.length === 0) {
      return this.setState({
        errorMessage: 'Please make sure month, year and locations are selected',
      })
    }

    fromCurrentStepToNextStep()
  }

  setMonthAndYear = (month: monthType, year: number) => {
    const { complianceStore } = this.props
    complianceStore.changeMonth(month)
    complianceStore.changeYear(year)
    this.onClickNext()
  }

  getYearsUntilNow = (startYear?: number) => {
    const current = new Date().getFullYear()
    const years = []
    startYear = startYear || 2018
    while (startYear <= current) {
      const singleYear = startYear++
      years.push({ value: singleYear, label: `${singleYear}` })
    }
    return years
  }

  render() {
    const { errorMessage } = this.state
    const { complianceStore, currentCompanyStore } = this.props
    const locations = currentCompanyStore?.locations || []
    const province = currentCompanyStore?.company?.province

    const showOptionForQuarterlyReport = province === CompaniesModels.Province.MB

    return (
      <div className='row'>
        <div className='col-md-6'>
          <div>
            <div className='mb-4'>
              <h5>Select Locations</h5>
              <div className='text-muted mb-4'>
                If you have a front/back separation, you must select both locations.
              </div>
              {locations.map((location, index) => {
                const checked = complianceStore.locationIds.includes(location.id)
                const id = `${location.id}-${location.name}-${index}`
                return (
                  <div key={location.id}>
                    <Checkbox
                      id={id}
                      checked={checked}
                      onChange={() => complianceStore.toggleLocationId(location.id)}
                    >
                      {location.name}
                    </Checkbox>
                  </div>
                )
              })}
            </div>
          </div>
          {complianceStore.locationIds.length > 0 && showOptionForQuarterlyReport && (
            <>
              <h6>Report Type</h6>
              <Select<BaseComplianceModels.ComplianceTimeFrame>
                placeholder='Select...'
                style={{
                  width: 100,
                }}
                onSelect={(newTimeFrame) => {
                  complianceStore.changeTimeFrame(newTimeFrame)
                  complianceStore.changeMonth(1)
                  this.setState({
                    errorMessage: '',
                  })
                }}
                defaultValue={complianceStore.timeFrame}
                options={[
                  { value: BaseComplianceModels.ComplianceTimeFrame.MONTHLY, label: 'Monthly' },
                  {
                    value: BaseComplianceModels.ComplianceTimeFrame.QUARTERLY,
                    label: 'Quarterly',
                  },
                ]}
              />

              {complianceStore.timeFrame === BaseComplianceModels.ComplianceTimeFrame.MONTHLY && (
                <ComplianceMonthSelector
                  complianceStore={complianceStore}
                  onClickNext={this.onClickNext}
                  setMonthAndYear={this.setMonthAndYear}
                  getYearsUntilNow={this.getYearsUntilNow}
                />
              )}

              {complianceStore.timeFrame === BaseComplianceModels.ComplianceTimeFrame.QUARTERLY && (
                <ComplianceQuarterSelector
                  complianceStore={complianceStore}
                  onClickNext={this.onClickNext}
                  setMonthAndYear={this.setMonthAndYear}
                  getYearsUntilNow={this.getYearsUntilNow}
                />
              )}
            </>
          )}

          {complianceStore.locationIds.length > 0 && !showOptionForQuarterlyReport && (
            <ComplianceMonthSelector
              complianceStore={complianceStore}
              onClickNext={this.onClickNext}
              setMonthAndYear={this.setMonthAndYear}
              getYearsUntilNow={this.getYearsUntilNow}
            />
          )}
          <div className='mt-3'>
            <FormError message={errorMessage} />
          </div>
        </div>

        <div className='col-md-6'>
          <ComplianceInfo />
        </div>
      </div>
    )
  }
}
